<template>
  <div class="agent-upload-page">
    <div class="agent-upload-inner">
      <header class="page-header">
        <h1>{{ confirmationRequired ? 'Confirm this upload' : 'Reviewing your document' }}</h1>
        <p class="purpose-lead">
          <template v-if="confirmationRequired">
            This certificate may not match the entity this request was sent for.
            You can upload a different file or confirm and proceed against the
            originally requested entity only — no stores will be added or reassigned.
          </template>
          <template v-else>
            Checking this certificate against the project’s insurance requirements.
            You’ll see what passed and any items that still need attention.
          </template>
        </p>
      </header>

      <section v-if="confirmationRequired && preflight" class="confirmation-panel">
        <h2>Identity check</h2>
        <dl class="preflight-grid">
          <div>
            <dt>Requested entity</dt>
            <dd>{{ preflight.requested_entity?.name || 'Unknown' }}</dd>
          </div>
          <div v-if="preflight.primary_certificate_entity_name">
            <dt>Named insured on certificate</dt>
            <dd>{{ preflight.primary_certificate_entity_name }}</dd>
          </div>
          <div v-if="preflight.certificate_store_numbers?.length">
            <dt>Store numbers on certificate</dt>
            <dd>{{ formatStoreList(preflight.certificate_store_numbers) }}</dd>
          </div>
          <div v-if="preflight.alien_store_numbers?.length">
            <dt>Stores owned by another franchisee</dt>
            <dd class="warn">{{ formatStoreList(preflight.alien_store_numbers) }}</dd>
          </div>
        </dl>

        <div
          v-if="preflight.location_completeness?.missing_locations?.length"
          class="missing-locations"
        >
          <h3>Eligible locations not on this certificate</h3>
          <ul>
            <li
              v-for="loc in preflight.location_completeness.missing_locations"
              :key="loc.store_number || loc.location_id"
            >
              Store #{{ loc.store_number }}
              <span v-if="loc.address"> — {{ loc.address }}</span>
            </li>
          </ul>
          <p class="hint">
            If you proceed, review results will still flag these missing locations.
          </p>
        </div>

        <p v-if="preflight.message" class="warning-copy">{{ preflight.message }}</p>

        <div class="confirmation-actions">
          <button type="button" class="btn-secondary" :disabled="confirming" @click="retryAnalysis">
            Upload a different document
          </button>
          <button type="button" class="btn-primary" :disabled="confirming" @click="confirmAndProceed">
            {{ confirming ? 'Starting review…' : 'Yes, process for the requested entity' }}
          </button>
        </div>
        <p v-if="confirmError" class="error-text">{{ confirmError }}</p>
      </section>

      <template v-else>
        <section class="purpose-panel">
          <div class="purpose-icon" aria-hidden="true">
            <i class="fas fa-cog fa-spin"></i>
          </div>
          <div class="purpose-body">
            <h2>Document review</h2>
            <p v-if="documentName" class="document-name">{{ documentName }}</p>
            <p class="page-meta">Usually 30–90 seconds.</p>
          </div>
        </section>

        <div class="analysis-card">
          <div class="progress-steps">
            <div class="step" :class="{ 'active': currentStep >= 1, 'completed': currentStep > 1 }">
              <div class="step-icon">
                <i class="fas fa-search" v-if="currentStep < 2"></i>
                <i class="fas fa-check" v-else></i>
              </div>
              <div class="step-content">
                <h5>Reading the document</h5>
                <p>Extracting coverage, limits, and endorsements</p>
              </div>
            </div>

            <div class="step" :class="{ 'active': currentStep >= 2, 'completed': currentStep > 2 }">
              <div class="step-icon">
                <i class="fas fa-list-check" v-if="currentStep < 3"></i>
                <i class="fas fa-check" v-else></i>
              </div>
              <div class="step-content">
                <h5>Matching requirements</h5>
                <p>Mapping against this project’s check</p>
              </div>
            </div>

            <div class="step" :class="{ 'active': currentStep >= 3, 'completed': currentStep > 3 }">
              <div class="step-icon">
                <i class="fas fa-shield-alt" v-if="currentStep < 4"></i>
                <i class="fas fa-check" v-else></i>
              </div>
              <div class="step-content">
                <h5>Evaluating coverage</h5>
                <p>Checking limits, dates, and required endorsements</p>
              </div>
            </div>

            <div class="step" :class="{ 'active': currentStep >= 4, 'completed': currentStep > 4 }">
              <div class="step-icon">
                <i class="fas fa-file-alt" v-if="currentStep < 5"></i>
                <i class="fas fa-check" v-else></i>
              </div>
              <div class="step-content">
                <h5>Preparing your response</h5>
                <p>Clear pass/fail items and next actions</p>
              </div>
            </div>
          </div>

          <div v-if="analysisError" class="error-state">
            <h4>Review could not finish</h4>
            <p>{{ analysisError }}</p>
            <button @click="retryAnalysis" class="btn-primary">
              Upload a different document
            </button>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import api from '@/services/api';
import { formatStoreList, isConfirmationRequiredPoll } from '@/utils/secureUploadPreflight';

export default {
  name: 'SecureUploadAnalysis',
  data() {
    return {
      token: null,
      documentId: null,
      sessionId: null,
      checkRunId: null,
      documentName: '',

      currentStep: 1,
      stepInterval: null,
      pollInterval: null,
      pollAttempts: 0,
      maxPollAttempts: 60,

      analysisError: null,
      confirmationRequired: false,
      preflight: null,
      confirming: false,
      confirmError: null,
    };
  },

  created() {
    this.token = this.$route.params.token;
    this.documentId = this.$route.query.documentId;
    this.sessionId = this.$route.query.sessionId;
    this.checkRunId = this.$route.query.checkRunId;

    this.startAnalysisFlow();
  },

  beforeUnmount() {
    this.stopPolling();
    this.stopStepAnimation();
  },

  methods: {
    formatStoreList,

    startAnalysisFlow() {
      this.startStepAnimation();
      this.startResultsPolling();
    },

    startStepAnimation() {
      this.stepInterval = setInterval(() => {
        if (this.currentStep < 4) {
          this.currentStep += 1;
        }
      }, 15000);
    },

    stopStepAnimation() {
      if (this.stepInterval) {
        clearInterval(this.stepInterval);
        this.stepInterval = null;
      }
    },

    startResultsPolling() {
      this.pollAttempts = 0;
      this.pollInterval = setInterval(() => {
        this.pollForResults();
      }, 5000);
      this.pollForResults();
    },

    stopPolling() {
      if (this.pollInterval) {
        clearInterval(this.pollInterval);
        this.pollInterval = null;
      }
    },

    async pollForResults() {
      try {
        this.pollAttempts += 1;

        if (this.pollAttempts > this.maxPollAttempts) {
          this.stopPolling();
          this.analysisError = 'Analysis is taking longer than expected. Please refresh the page to check for results.';
          return;
        }

        const response = await api.get(`/secure-upload/${this.token}/results/`);
        const responseData = response.data;

        if (isConfirmationRequiredPoll(responseData)) {
          this.stopPolling();
          this.stopStepAnimation();
          this.confirmationRequired = true;
          this.preflight = responseData.preflight || null;
          this.documentId = responseData.document_id || this.documentId;
          this.documentName = responseData.document_name || this.documentName;
          return;
        }

        if (responseData.status === 'rejected') {
          this.stopPolling();
          this.stopStepAnimation();
          this.analysisError = responseData.message || 'Your document was not accepted for this project. Please upload the correct document type.';
          return;
        }

        if (responseData.status === 'failed') {
          this.stopPolling();
          this.stopStepAnimation();
          this.analysisError = responseData.message || 'Document processing encountered an error. Please try uploading again.';
          return;
        }

        if (responseData.status === 'success') {
          const checkRun = responseData.check_run;
          this.documentName = responseData.document_name;

          if (checkRun.status === 'COMPLETED') {
            this.stopPolling();
            this.stopStepAnimation();
            this.currentStep = 5;

            setTimeout(() => {
              this.$router.push({
                name: 'SecureUploadResults',
                params: { token: this.token },
                query: { checkRunId: checkRun.id },
              });
            }, 1500);
          } else if (checkRun.status === 'FAILED') {
            this.stopPolling();
            this.stopStepAnimation();
            this.analysisError = (
              checkRun.results_summary?.error
              || 'Analysis failed. Please try uploading again.'
            );
          }
        }
      } catch (error) {
        if (this.pollAttempts > 5) {
          this.analysisError = 'Having trouble checking analysis status. Will continue trying…';
        }
      }
    },

    async confirmAndProceed() {
      if (!this.documentId) {
        this.confirmError = 'Missing document reference. Please upload again.';
        return;
      }
      this.confirming = true;
      this.confirmError = null;
      try {
        const response = await api.post(
          `/secure-upload/${this.token}/confirm-preflight/`,
          { document_id: this.documentId },
        );
        if (response.data.status === 'success') {
          this.confirmationRequired = false;
          this.preflight = null;
          this.analysisError = null;
          this.currentStep = 1;
          this.startAnalysisFlow();
        } else {
          this.confirmError = response.data.message || 'Could not confirm upload.';
        }
      } catch (err) {
        this.confirmError = err.response?.data?.message || 'Could not confirm upload. Please try again.';
      } finally {
        this.confirming = false;
      }
    },

    retryAnalysis() {
      this.$router.push({
        name: 'SecureUploadLanding',
        params: { token: this.token },
      });
    },
  },
};
</script>

<style scoped>
.agent-upload-page {
  min-height: 100vh;
  background: #f8fafc;
  color: #0f172a;
}

.agent-upload-inner {
  max-width: 40rem;
  margin: 0 auto;
  padding: 2rem 1.25rem 3rem;
}

.page-header h1 {
  font-size: 1.75rem;
  margin: 0 0 0.5rem;
}

.purpose-lead {
  color: #475569;
  line-height: 1.5;
}

.confirmation-panel {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-left: 4px solid #ea580c;
  border-radius: 8px;
  padding: 1.25rem 1.5rem;
  margin-top: 1.5rem;
}

.confirmation-panel h2 {
  margin: 0 0 1rem;
  font-size: 1.125rem;
}

.preflight-grid {
  display: grid;
  gap: 0.75rem;
  margin: 0 0 1rem;
}

.preflight-grid dt {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #64748b;
}

.preflight-grid dd {
  margin: 0.15rem 0 0;
  font-weight: 500;
}

.preflight-grid dd.warn {
  color: #b45309;
}

.missing-locations {
  margin: 1rem 0;
  padding: 0.75rem 1rem;
  background: #fff7ed;
  border-radius: 6px;
}

.missing-locations ul {
  margin: 0.5rem 0 0;
  padding-left: 1.25rem;
}

.hint,
.warning-copy {
  color: #475569;
  font-size: 0.9rem;
}

.confirmation-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-top: 1.25rem;
}

.btn-primary,
.btn-secondary {
  border-radius: 6px;
  padding: 0.65rem 1rem;
  font-weight: 600;
  cursor: pointer;
  border: none;
}

.btn-primary {
  background: #0f172a;
  color: #fff;
}

.btn-secondary {
  background: #e2e8f0;
  color: #0f172a;
}

.error-text {
  color: #b91c1c;
  margin-top: 0.75rem;
}

.purpose-panel,
.analysis-card {
  margin-top: 1.5rem;
}

.purpose-panel {
  display: flex;
  gap: 1rem;
  align-items: flex-start;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 1.25rem;
}

.purpose-icon {
  font-size: 1.5rem;
  color: #64748b;
}

.document-name {
  font-weight: 600;
}

.page-meta {
  color: #64748b;
  font-size: 0.9rem;
}

.progress-steps {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.step {
  display: flex;
  gap: 1rem;
  opacity: 0.5;
}

.step.active,
.step.completed {
  opacity: 1;
}

.step-icon {
  width: 2rem;
  text-align: center;
  color: #0f172a;
}

.error-state {
  margin-top: 1.5rem;
  padding: 1rem;
  background: #fef2f2;
  border-radius: 8px;
}
</style>
